import { computed, readonly, ref } from 'vue'
import { getApiLink } from '@/utils/api-urls'
import useTimer from '@/composables/timer/useTimer'
import type { Message } from './types'
import type { Mode } from '@/utils/api-urls'
import { promiseTimeout, useEventBus } from '@vueuse/core'
import { findLastIndex, propEq } from 'ramda'
import useNotifications from '@/composables/notifications/useNotifications'
import { useI18n } from '@/composables/i18n/useI18n'
import useEmbed from '@/embed/code'
import { logger } from '@sentry/vue'
import { NonActionableWarning } from '@/utils/sentry'

export type TroubleshootingInfo = {
    title: string
    intro: string
    steps: string
}

export class WebSocketConnectionError extends NonActionableWarning {
    code: number
    reason: string
    troubleshooting?: TroubleshootingInfo

    constructor(code: number, reason: string) {
        super('WebSocket connection failed')
        this.code = code
        this.reason = reason

        if (code === 1006) {
            this.troubleshooting = {
                title: 'views.widget.socket_error_1006.troubleshooting_title',
                intro: 'views.widget.socket_error_1006.troubleshooting_intro',
                steps: 'views.widget.socket_error_1006.steps',
            }
        }
    }
}

const { pushNotification } = useNotifications()

export enum WEBSOCKET_STATUS {
    connecting,
    open,
    closing,
    closed,
}

const MAX_ATTEMPTS = 5
const WEBSOCKET_CLOSED_CODE = 1000

let timer_id: NodeJS.Timeout | undefined
let attempts = 0

const websocketInstance = ref<WebSocket | undefined>()
const messages = ref<Message[]>([])
const firstMessage = ref<Message>()

const handleWebsocketMessage = (m: MessageEvent) => {
    const payload = JSON.parse(m.data)

    useEventBus('websocket-message').emit(payload)

    // prevent further handling "forbidden" in response from healthcheck
    if (payload?.message?.toLowerCase() === 'forbidden') {
        return
    }

    if (!messages.value.length) {
        firstMessage.value = payload
        useEmbed().setWorkflowExecutionPhase('running')
    }

    if ('scanResultStatus' in payload && 'isMalicious' in payload) {
        return
    }

    messages.value.push(payload)
}

const useWebsocket = () => {
    const initialize = (token: string, workflowId: string, mode: Mode) => new Promise<void>((resolve, reject) => {
        const socketUrl = getApiLink('ws', mode)
        websocketInstance.value = new WebSocket(`${socketUrl}?token=${token}&workflowId=${workflowId}`)

        websocketInstance.value.addEventListener('open', () => {
            if (timer_id) {
                clearTimeout(timer_id)
            }
            ping()
            resolve()
        })

        websocketInstance.value.addEventListener('error', () => {
            if (timer_id) {
                clearTimeout(timer_id)
            }
        })

        websocketInstance.value.addEventListener('close', (arg) => {
            if (arg.code === WEBSOCKET_CLOSED_CODE) {
                return
            }

            // eslint-disable-next-line no-console
            console.error(`Socket closed. Reconnecting - attempt: ${attempts}`)

            if (attempts >= MAX_ATTEMPTS) {
                clearTimeout(timer_id)
                reject(new WebSocketConnectionError(arg.code, arg.reason))
                return
            }

            logger.warn('WebSocket closed unexpectedly', {
                code: arg.code,
                reason: arg.reason,
                wasClean: arg.wasClean,
                attempts,
            })

            timer_id = setTimeout(() => {
                attempts += 1

                resolve(initialize(token, workflowId, mode))
            }, (1000 * (attempts + 1)))
        })

        websocketInstance.value.addEventListener('message', handleWebsocketMessage)
    })

    const ping = () => {
        if (!websocketInstance.value || websocketInstance.value.readyState !== WEBSOCKET_STATUS.open) {
            return
        }
        websocketInstance.value.send('ping')
        setTimeout(ping, 15000)
    }

    const send = (message: string | ArrayBufferLike | Blob | ArrayBufferView) => {
        if (websocketInstance.value?.readyState === WEBSOCKET_STATUS.open) {
            websocketInstance.value.send(message)
        }
    }

    const getMessageIndexByTaskId = (id: string) => {
        return findLastIndex(propEq(id, 'taskId'), messages.value)
    }

    const waitForNewMessage = async (prevMessageId: string) => {
        const { t } = useI18n()
        const { startTimer, resetTimer, time } = useTimer(30)
        startTimer()

        const currentIndex = getMessageIndexByTaskId(prevMessageId)
        let tmp
        let flagLonger = false
        let newMessage: Message | undefined

        while (!newMessage) {
            await promiseTimeout(250)

            // Notifications
            if (time.value % 3 === 0 && tmp !== time.value) {
                tmp = time.value
            }

            if (time.value > 20 && !flagLonger) {
                flagLonger = true
                pushNotification({
                    type: 'warning',
                    subtype: 'wait',
                    content: t('components.notifications.longer_than_expected'),
                })
            }

            newMessage = messages.value.slice(currentIndex).find(t => t.taskId !== prevMessageId)
        }

        resetTimer()
        return newMessage
    }

    const waitForNewMessageByCount = async (taskId: string, previousCount: number) => {
        const { t } = useI18n()
        const { startTimer, resetTimer, time } = useTimer(30)
        startTimer()

        let flagLonger = false
        let newMessage: Message | undefined

        while (!newMessage) {
            await promiseTimeout(250)

            if (time.value > 20 && !flagLonger) {
                flagLonger = true
                pushNotification({
                    type: 'warning',
                    subtype: 'wait',
                    content: t('components.notifications.longer_than_expected'),
                })
            }

            const matching = messages.value.filter(m => m.taskId === taskId)
            if (matching.length > previousCount) {
                newMessage = matching[matching.length - 1]
            }
        }

        resetTimer()
        return newMessage
    }

    const replaceMessagesByTaskId = (taskId: string, oldCount: number) => {
        let skipped = 0
        messages.value = messages.value.filter(m => {
            if (m.taskId !== taskId) return true
            skipped++
            return skipped > oldCount
        })
    }

    const reset = () => {
        websocketInstance.value?.close(WEBSOCKET_CLOSED_CODE)
        websocketInstance.value = undefined
        messages.value = []
        firstMessage.value = undefined
    }

    if (import.meta.env.DEV) {
        Object.assign(window, { tlWebsocketInstance: websocketInstance })
    }

    return {
        initialize,
        send,
        ping,
        firstMessage: readonly(firstMessage),
        websocketInstance,
        messages: readonly(messages),
        latestMessage: computed(() => messages.value[messages.value.length - 1]),
        waitForNewMessage,
        waitForNewMessageByCount,
        replaceMessagesByTaskId,
        reset,
        handleWebsocketMessage,
    }
}

export default useWebsocket
